/**
 * The coach (spec §17): a TRANSPARENT rules engine, not an AI. It scans the
 * projection output and surfaces observations — each states what it found,
 * why it matters, and what to change. Ranked by estimated dollar impact where
 * computable. Never a recommendation to buy a product.
 */
import type { Plan } from '../model/types';
import { ACCOUNT_META } from '../model/accountMeta';
import { activeAssumptions, type ProjectionResult } from './projection';
import { federalTaxYear } from './data/federal';
import { IRMAA_TIERS_2026 } from './data/medicare';
import { contributionLimits } from './data/contributionLimits';
import { amortize } from './amortization';
import { CURRENT_YEAR } from '../model/defaults';

export interface CoachObservation {
  key: string;
  title: string;
  /** What was found, in one sentence. */
  found: string;
  /** Why it matters. */
  why: string;
  /** What to change. */
  action: string;
  /** Estimated dollar impact (annual or total, as labeled) when computable. */
  impact?: number;
  impactLabel?: string;
}

export interface CoachInput {
  plan: Plan;
  projection: ProjectionResult;
  claimingDeltaNetWorth?: number;
}

const usdC = (n: number) =>
  Math.abs(n) >= 999_500 ? `$${(n / 1e6).toFixed(2).replace(/\.?0+$/, '')}M`
  : Math.abs(n) >= 1_000 ? `$${Math.round(n / 1000)}K` : `$${Math.round(n).toLocaleString('en-US')}`;

export function runCoach({ plan, projection, claimingDeltaNetWorth }: CoachInput): CoachObservation[] {
  const out: CoachObservation[] = [];
  const snaps = projection.snapshots;
  const first = snaps[0]!;
  const person = plan.household.people[0]!;
  const working = first.wagesIncome + first.selfEmploymentIncome > 0;

  // ── Unclaimed employer match ──────────────────────────────────────────────
  // Straight from the engine: each person measured against their own wages.
  if (working) {
    const left = first.employerMatchUnclaimed;
    if (left >= 100) {
      out.push({
        key: 'match', title: 'Free money is being declined',
        found: `Contributions are below the employer-match threshold, leaving ${usdC(left)} a year unclaimed.`,
        why: 'A match is an instant 50-100% return — nothing else in this plan compounds like it.',
        action: 'Raise the 401(k) contribution to at least the match threshold.',
        impact: left, impactLabel: 'per year',
      });
    }
  }

  // ── Contributions below the limit while cash sits idle ────────────────────
  if (working) {
    const L = contributionLimits(CURRENT_YEAR);
    const age = CURRENT_YEAR - person.birthYear;
    const limit = L.employer.electiveDeferral +
      (age >= 60 && age <= 63 ? L.employer.superCatchUp60to63 : age >= 50 ? L.employer.catchUp50 : 0);
    const idle = plan.accounts
      .filter((a) => a.type === 'checking_savings')
      .reduce((t, a) => t + a.balance, 0);
    const monthlyMust = (first.essentialSpending + first.debtService) / 12;
    const room = limit - first.employeeContributions;
    if (room > 2_000 && idle > monthlyMust * 9) {
      out.push({
        key: 'idle_cash', title: 'Tax-advantaged space unused while cash sits idle',
        found: `${usdC(room)} of contribution room is unused while ${usdC(idle)} sits in cash — well beyond an emergency fund.`,
        why: 'Cash above the emergency fund earns taxable interest; the same dollars deferred grow tax-advantaged.',
        action: 'Raise deferrals and live off the excess cash for the rest of the year.',
        impact: room, impactLabel: 'of space per year',
      });
    }
  }

  // ── Conversion opportunity: room left in low brackets ─────────────────────
  const lowYears = snaps.filter((s) => {
    if (s.wagesIncome + s.selfEmploymentIncome > 0) return false;
    if (s.rothConversionAmount > 0) return false;
    const fy = federalTaxYear(s.year, activeAssumptions(plan).generalInflation);
    const b12top = fy.ordinaryBrackets[s.filingStatusUsed][2]?.floor ?? 50_400;
    return s.federal.ordinaryTaxableIncome < b12top - 15_000;
  });
  const pretaxTotal = plan.accounts
    .filter((a) => ACCOUNT_META[a.type].taxFamily === 'pretax')
    .reduce((t, a) => t + a.balance, 0);
  if (lowYears.length >= 3 && pretaxTotal > 200_000 && !plan.decisions.rothConversionRule) {
    out.push({
      key: 'conversion_window', title: 'Low-tax years are going unused',
      found: `${lowYears.length} projected years sit far below the top of the 12% bracket while ${usdC(pretaxTotal)} waits in pre-tax accounts.`,
      why: 'Those brackets expire each year. Filling them with Roth conversions moves money out at 10-12% instead of the higher rates RMDs force later.',
      action: 'Open the Roth explorer and compare the fill-the-bracket strategies.',
    });
  }

  // ── IRMAA proximity ───────────────────────────────────────────────────────
  const joint = plan.household.filingStatus === 'mfj' || plan.household.filingStatus === 'qw';
  const irmaaNear = snaps.filter((s) => {
    const age = s.ages[person.id] ?? 0;
    if (age < 63) return false; // MAGI starts mattering 2 years before 65
    // The tiers index with CPI; compare each year's nominal AGI against
    // floors grown by the same cumulative inflation the projection used.
    return IRMAA_TIERS_2026.slice(1).some((t) => {
      const floor = (joint ? t.magiFloorMfj : t.magiFloorSingle) * s.cumulativeInflationFactor;
      return s.federal.agi > floor - 8_000 && s.federal.agi <= floor;
    });
  });
  if (irmaaNear.length > 0) {
    out.push({
      key: 'irmaa_near', title: 'Within a few thousand dollars of a Medicare surcharge cliff',
      found: `In ${irmaaNear.length} ${irmaaNear.length === 1 ? 'year' : 'years'} (first: ${irmaaNear[0]!.year}), income lands just under an IRMAA threshold.`,
      why: 'IRMAA is a cliff: one dollar over costs the full surcharge two years later — roughly $1,000-2,900/person/year.',
      action: 'Keep a small buffer when sizing withdrawals or conversions in those years.',
    });
  }

  // ── ACA cliff ─────────────────────────────────────────────────────────────
  const overCliff = snaps.filter((s) => s.acaFplRatio != null && s.acaFplRatio > 4.0);
  const nearCliff = snaps.filter((s) => s.acaFplRatio != null && s.acaFplRatio >= 3.5 && s.acaFplRatio <= 4.0);
  if (overCliff.length > 0) {
    const lost = overCliff.length * 12_000; // rough benchmark-premium order of magnitude
    out.push({
      key: 'aca_over', title: 'Health-insurance subsidy lost to the income cliff',
      found: `${overCliff.length} pre-Medicare ${overCliff.length === 1 ? 'year is' : 'years are'} over 400% of the poverty level — no premium credit at all.`,
      why: 'For 2026 the cliff is back: crossing it forfeits the entire subsidy, often five figures per year.',
      action: 'Source those years from savings or Roth instead of pre-tax withdrawals to pull income under the line.',
      impact: lost, impactLabel: 'order of magnitude at stake',
    });
  } else if (nearCliff.length > 0) {
    out.push({
      key: 'aca_near', title: 'Close to the health-subsidy cliff',
      found: `${nearCliff.length} pre-Medicare ${nearCliff.length === 1 ? 'year sits' : 'years sit'} within reach of 400% of the poverty level.`,
      why: 'One dollar over forfeits the entire premium credit for that year.',
      action: 'Watch withdrawal sizing and defer any Roth conversions in those years.',
    });
  }

  // ── 0% capital-gains room ─────────────────────────────────────────────────
  const zeroLtcgYears = snaps.filter((s) => {
    if (s.wagesIncome + s.selfEmploymentIncome > 0) return false;
    const fy = federalTaxYear(s.year, activeAssumptions(plan).generalInflation);
    const ceiling = fy.ltcgBreakpoints[s.filingStatusUsed].rate0Ceiling;
    return s.federal.taxableIncome < ceiling - 20_000;
  });
  const brokerageGain = plan.accounts
    .filter((a) => a.type === 'brokerage')
    .reduce((t, a) => t + Math.max(0, a.balance - (a.costBasis ?? a.balance)), 0);
  if (zeroLtcgYears.length >= 2 && brokerageGain > 50_000) {
    out.push({
      key: 'zero_ltcg', title: 'Capital gains could be realized at 0%',
      found: `${zeroLtcgYears.length} projected years have room in the 0% capital-gains bracket while the brokerage holds ${usdC(brokerageGain)} of unrealized gains.`,
      why: 'Selling and immediately rebuying in those years resets the cost basis for free — gains harvested at a 0% rate.',
      action: 'Plan gain harvesting in the low-income years (mind the ACA cliff if before 65).',
    });
  }

  // ── Early-withdrawal penalties ────────────────────────────────────────────
  // The engine already sequences to avoid these, so anything left is a real
  // gap in the bridge rather than a sequencing mistake — worth naming the
  // years and accounts precisely so the fix is obvious.
  const penaltyTotal = snaps.reduce((t, s) => t + s.earlyWithdrawalPenalty, 0);
  if (penaltyTotal > 1_000) {
    const penaltyYears = snaps.filter((s) => s.earlyWithdrawalPenalty > 0);
    const first = penaltyYears[0]!;
    const last = penaltyYears[penaltyYears.length - 1]!;
    const span = first.year === last.year ? `${first.year}` : `${first.year}-${last.year}`;

    // Which accounts are actually being hit, worst first.
    const byAccount = new Map<string, number>();
    for (const s of penaltyYears) {
      for (const f of s.accounts) {
        if (f.penalty > 0) byAccount.set(f.label, (byAccount.get(f.label) ?? 0) + f.penalty);
      }
    }
    const worst = [...byAccount.entries()].sort((x, y) => y[1] - x[1]).slice(0, 2);
    const named = worst.map(([label, amt]) => `${label} (${usdC(amt)})`).join(' and ');

    // The fixes that actually apply to THIS plan, in order of leverage.
    const fixes: string[] = [];
    const hasLadder = plan.decisions.rothConversionRule != null;
    const hasRoth = plan.accounts.some((a) => ACCOUNT_META[a.type].taxFamily === 'roth');
    const employerPlanUnflagged = plan.accounts.some(
      (a) => ACCOUNT_META[a.type].rule55Possible && !a.currentEmployerPlan && a.balance > 0,
    );
    const firstPenaltyAge = Math.min(
      ...Object.values(first.ages).filter((v): v is number => typeof v === 'number'),
    );
    if (!hasLadder && hasRoth) {
      fixes.push('start a Roth conversion ladder five years before you need the money — each conversion becomes penalty-free at any age once it has seasoned');
    }
    if (employerPlanUnflagged && firstPenaltyAge >= 55) {
      fixes.push('if one of these is the employer plan you separate from at 55 or later, mark it as such on the account — the Rule of 55 makes it penalty-free immediately');
    }
    fixes.push('or build a bigger taxable bridge to cover the years before 59½');

    out.push({
      key: 'penalties', title: 'The plan still pays early-withdrawal penalties',
      found: `${usdC(penaltyTotal)} of 10% penalties across ${penaltyYears.length} ${penaltyYears.length === 1 ? 'year' : 'years'} (${span}), mostly from ${named || 'pre-tax accounts'}.`,
      why: 'Withdrawals are already sequenced to spend every penalty-free dollar first, so this is what is left over — the bridge to 59½ is genuinely short, and the 10% is pure loss on top of the income tax.',
      action: `Consider: ${fixes.join('; ')}.`,
      impact: penaltyTotal, impactLabel: 'total',
    });
  }

  // ── Emergency fund ────────────────────────────────────────────────────────
  const cash = plan.accounts
    .filter((a) => a.type === 'checking_savings' || a.type === 'cd_money_market')
    .reduce((t, a) => t + a.balance, 0);
  const monthlyMust =
    (first.essentialSpending + first.healthcarePremiums + first.healthcareOOP + first.debtService) / 12;
  if (monthlyMust > 0 && cash < monthlyMust * 3) {
    out.push({
      key: 'efund', title: 'Emergency fund below three months',
      found: `${usdC(cash)} of cash covers ${(cash / monthlyMust).toFixed(1)} months of essentials.`,
      why: 'Without a cash buffer, surprises force selling investments at whatever price the market offers that week.',
      action: `Build cash to at least ${usdC(monthlyMust * 3)} before other goals.`,
    });
  }

  // ── Guaranteed income gap ─────────────────────────────────────────────────
  const retSnaps = snaps.filter((s) => s.wagesIncome + s.selfEmploymentIncome === 0 && s.essentialSpending > 0);
  if (retSnaps.length > 0) {
    const avgGap =
      retSnaps.reduce((t, s) => {
        const must = s.essentialSpending + s.healthcarePremiums + s.healthcareOOP + s.ltcNetCost;
        return t + Math.max(0, must - s.guaranteedIncome);
      }, 0) / retSnaps.length;
    if (avgGap > 20_000) {
      out.push({
        key: 'guaranteed_gap', title: 'Essentials depend heavily on the portfolio',
        found: `On average ${usdC(avgGap)}/year of MUST-spend is not covered by guaranteed income.`,
        why: 'That gap has to be met every single year, including the bad ones.',
        action: 'Delaying Social Security (especially the higher earner) permanently raises the guaranteed floor.',
        impact: avgGap, impactLabel: 'per year uncovered',
      });
    }
  }

  // ── Claiming efficiency ───────────────────────────────────────────────────
  if (claimingDeltaNetWorth != null && claimingDeltaNetWorth > 10_000) {
    out.push({
      key: 'claiming', title: 'A better Social Security claiming age exists',
      found: `The optimizer finds ${usdC(claimingDeltaNetWorth)} more ending net worth at different claiming ages.`,
      why: 'Claiming age is one of the few one-time, irreversible decisions in the plan.',
      action: 'Review the heat map and click the starred combination to apply it.',
      impact: claimingDeltaNetWorth, impactLabel: 'ending net worth',
    });
  }

  // ── Concentrated pre-tax with large RMDs ──────────────────────────────────
  const maxRmd = Math.max(0, ...snaps.map((s) => s.totalRmd));
  if (maxRmd > 60_000 && !plan.decisions.rothConversionRule) {
    const peak = snaps.find((s) => s.totalRmd === maxRmd)!;
    out.push({
      key: 'rmd_heavy', title: 'RMDs will force large taxable income later',
      found: `Required distributions peak at ${usdC(maxRmd)} in ${peak.year} — income you must take whether needed or not.`,
      why: 'Forced income lands in higher brackets, drags more Social Security into taxability, and can trigger IRMAA.',
      action: 'The Roth explorer shows what draining pre-tax earlier at lower rates is worth.',
    });
  }

  // ── Mortgage past retirement ──────────────────────────────────────────────
  const retirementYear = person.birthYear + person.retirementAge;
  for (const d of plan.debts) {
    if (d.type !== 'mortgage' || d.balance <= 0) continue;
    const a = amortize(d.balance, d.annualRate, d.monthlyPayment);
    if (a.payoffMonths == null) continue;
    const payoffYear = CURRENT_YEAR + Math.ceil(a.payoffMonths / 12);
    if (payoffYear > retirementYear + 1) {
      const extra = amortize(d.balance, d.annualRate, d.monthlyPayment + 300);
      const saved = a.totalInterest - (extra.totalInterest ?? 0);
      out.push({
        key: `mortgage_${d.id}`, title: 'The mortgage outlives the paycheck',
        found: `"${d.label}" pays off around ${payoffYear}, ${payoffYear - retirementYear} years into retirement.`,
        why: 'Fixed debt service in retirement raises the must-spend floor exactly when income becomes variable.',
        action: `An extra $300/month clears it sooner and saves about ${usdC(saved)} of interest.`,
        impact: saved, impactLabel: 'interest saved',
      });
    }
  }

  // ── No LTC modeled ────────────────────────────────────────────────────────
  if (!(plan.ltcEvents ?? []).some((e) => e.enabled)) {
    out.push({
      key: 'no_ltc', title: 'No long-term-care provision is modeled',
      found: 'The plan assumes zero paid care, which is the lucky case, not the median one.',
      why: 'A few years of assisted living runs well into six figures and typically lands late, when options are fewest.',
      action: 'Toggle a care event in Healthcare to see whether the plan absorbs it — then decide about insurance deliberately.',
    });
  }

  // ── Stale balances ────────────────────────────────────────────────────────
  const stale = plan.accounts.filter((a) => {
    if (!a.asOfDate) return false;
    const d = new Date(a.asOfDate).getTime();
    return Number.isFinite(d) && Date.now() - d > 365 * 24 * 3600 * 1000;
  });
  if (stale.length > 0) {
    out.push({
      key: 'stale', title: 'Account balances are over a year old',
      found: `${stale.length} ${stale.length === 1 ? 'account has' : 'accounts have'} not been updated in 12+ months (${stale.map((a) => a.label).join(', ')}).`,
      why: 'Every projection downstream inherits the staleness.',
      action: 'Refresh the balances and as-of dates in Accounts.',
    });
  }

  // ── Failure year with contributing factors ────────────────────────────────
  if (projection.firstDepletionYear) {
    const s = snaps.find((x) => x.year === projection.firstDepletionYear)!;
    const factors: [string, number][] = [
      ['spending', s.totalSpending],
      ['healthcare + LTC', s.healthcarePremiums + s.healthcareOOP + s.ltcNetCost],
      ['taxes', s.priorYearTaxPaid],
      ['debt service', s.debtService],
    ];
    factors.sort((a, b) => b[1] - a[1]);
    out.push({
      key: 'depletion', title: `The money runs out in ${projection.firstDepletionYear}`,
      found: `At age ${projection.firstDepletionYear - person.birthYear}, spending can no longer be funded.`,
      why: `The two largest outflows that year: ${factors[0]![0]} (${usdC(factors[0]![1])}) and ${factors[1]![0]} (${usdC(factors[1]![1])}).`,
      action: 'The "What can I actually afford?" card shows the spending level that lasts; retirement age and claiming age are the other big levers.',
    });
  }

  // Rank: computable impact first (descending), then the rest.
  return out.sort((a, b) => (b.impact ?? -1) - (a.impact ?? -1));
}
